import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { PageHelper } from 'Components/PageHelper';
import { deleteCookie, getCookies } from 'services/cookies-service';
import { tableHeader } from './table-header.js';
import { useAuthTenant } from 'store/auth';

import { DefaultTable } from 'Components/DefaultTable';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';

import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
// import { HttpStatus } from 'utils/http_status_utils.js';
import { Column, Row } from 'Components/Grid/index.js';

export const Cookies = () => {
  const { t } = useTranslation(['cookies']);
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const navigate = useNavigate();
  const {
    isLoading,
    error,
    data: cookie,
  } = useQuery(['getCookies', reFetch], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getCookies(selectedTenant.slug);
    if (result?.status === 200) {
      return result?.data;
    }
    return {
      totalizer: {
        opt_ins: 0,
        opt_out: 0,
        unique_user: 0,
      },
      disclaimers: [],
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const mutationDelete = useMutation(
    id => {
      return deleteCookie(id);
    },
    {
      onError: (error) => {
        const errorMessage = error.response.data.message || error.message;
        // An error happened!
        SwalReact.fire({
          title: t('cookies:page.swal.errorTitle'),
          text: errorMessage,
          icon: 'error',
        });
      },
      onSuccess: () => {
        onClose();
        setRefetch(!reFetch);
        SwalReact.fire({
          title: t('cookies:page.swal.successTitle'),
          text: t('cookies:page.swal.deleteSuccessText'),
          icon: 'success',
        });
      },
    },
  );

  const onConfirm = () => {
    mutationDelete.mutate(itemToDelete?.id);
  };

  const tourSteps = [
    {
      target: 'body',
      content: t('cookies:page.tour.step1'),
      disableBeacon: true,
      disableScrolling: true,
      placement: 'center',
    },
    {
      target: '[data-tour="page-title"]',
      content: t('cookies:page.tour.step2'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="add-button"]',
      content: t('cookies:page.tour.step3'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="info-card"]',
      content: t('cookies:page.tour.step4'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="totalizers"]',
      content: t('cookies:page.tour.step5'),
      disableScrolling: false,
    },
    {
      target: '[data-tour="table"]',
      content: t('cookies:page.tour.step6'),
      disableScrolling: false,
    },
    {
      target: 'body',
      content: t('cookies:page.tour.step7'),
      disableScrolling: true,
      placement: 'center',
    },
  ];

  return (
    <Container
      title={t('cookies:page.title')}
      breadcrumb={t('cookies:page.breadcrumb')}
      showButtonAdd
      linkPage="/cookies"
      data-tour="page-title"
      addButtonDataTour="add-button"
    >
      <div data-tour="info-card">
      <Information
        title={t('cookies:page.title')}
        description={<>{t('cookies:page.description')}</>}
        style={{
          marginTop: '16px',
        }}
      />
      </div>
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('cookies:page.error')}</div>}
      {!isLoading && !error && cookie.disclaimers?.length === 0 && (
        <NoData description={t('cookies:page.empty')} />
      )}
      {!isLoading && !error && cookie.disclaimers?.length > 0 && (
        <div className="container">
          {/**
           * Aqui vai os totalizadores
           */}
          <div data-tour="totalizers">
          <Row className="mb-3">
            <Column>
              <Row justifyContent="center">
                <div className="mr-2">
                  {t('cookies:page.totalizers.uniqueVisitors')} <span className="font-weight-bold">{cookie.totalizer.unique_users}</span>
                </div>
                <div className="mr-2">
                  {t('cookies:page.totalizers.optIn')} <span className="font-weight-bold">{cookie.totalizer.opt_ins}</span>
                </div>
                <div>
                  {t('cookies:page.totalizers.optOut')} <span className="font-weight-bold">{cookie.totalizer.opt_out}</span>
                </div>
              </Row>
            </Column>
          </Row>
          </div>
          <div data-tour="table">
          <DefaultTable
            rows={tableHeader(t)}
            cells={cookie.disclaimers}
            onClickDelete={onDelete}
            link="cookies"
            onClickEdit={item => {
              navigate(`/cookies/edit/${item.id}`);
            }}
            onClickShow={item => {
              navigate(`/cookies/show/${item.id}`);
            }}
            onClick={() => {}}
          />
          </div>
          <ModalConfirmDelete
            description={t('cookies:page.deleteConfirm', { url: itemToDelete?.url })}
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
            loading={mutationDelete.isLoading}
          />
        </div>
      )}
      <PageHelper steps={tourSteps} tourKey="cookies-list" />
    </Container>
  );
};

